/**
 * Profile Saved Content (Search, Filter, Remove, Folder CRUD, Move)
 */
export const initSaved = (config) => {
    const savedSearchInput = document.getElementById('saved-search-input');
    const langToggleBtn = document.getElementById('ua-toggle-all-langs');
    const filterContainer = document.getElementById('saved-filter-container');
    const profileSavedContainer = document.querySelector('.profile-saved');
    const currentLang = config.lang || document.documentElement.lang || '';
    let showAllLangs = false;
    let searchDebounceTimer = null;

    if (!profileSavedContainer && !filterContainer) return;

    const adjustSavedTabCount = (folder, delta, isNative) => {
        if (!filterContainer) return;
        const targetFolder = folder || 'General';
        const btn = Array.from(filterContainer.querySelectorAll('.saved-filter__btn')).find(b => b.dataset.folder === targetFolder);
        if (btn) {
            const countSpan = btn.querySelector('.saved-filter__count');
            if (countSpan) {
                let total = parseInt(countSpan.dataset.total, 10) || 0;
                let native = parseInt(countSpan.dataset.native, 10) || 0;
                total = Math.max(0, total + delta);
                if (isNative) {
                    native = Math.max(0, native + delta);
                }
                countSpan.dataset.total = total;
                countSpan.dataset.native = native;
                countSpan.textContent = showAllLangs ? total : native;
            }
        }
    };

    const fetchSavedPage = async (page = 1, folder = null, lang = null, search = null) => {
        if (!profileSavedContainer) return;
        const activeBtn = filterContainer?.querySelector('.saved-filter__btn.is-active[data-folder]');
        const currentFolder = folder !== null ? folder : (activeBtn ? activeBtn.dataset.folder : (profileSavedContainer.dataset.activeFolder || 'all'));
        const currentLangParam = lang !== null ? lang : (showAllLangs ? 'all' : (profileSavedContainer.dataset.activeLang || currentLang));
        const currentSearch = search !== null ? search : (savedSearchInput ? savedSearchInput.value.trim() : '');

        const fd = new FormData();
        fd.append('action', 'united_auth_get_saved');
        fd.append('security', config.nonce);
        fd.append('page', page);
        fd.append('folder', currentFolder);
        fd.append('lang', currentLangParam);
        fd.append('search', currentSearch);

        try {
            const response = await fetch(config.ajax_url, { method: 'POST', body: fd });
            const result = await response.json();
            if (result.success && result.data) {
                let grid = document.getElementById('saved-items-grid');
                if (grid) {
                    if (result.data.html && result.data.html.trim().length > 0) {
                        grid.innerHTML = result.data.html;
                    } else {
                        grid.innerHTML = '<div class="profile-empty-state"><div class="profile-empty-state__icon"><i class="fa-solid fa-bookmark"></i></div><p>' + (config.i18n?.prof_saved_empty || 'Немає збережених матеріалів') + '</p></div>';
                    }
                }

                let pagWrapper = profileSavedContainer.querySelector('.ua-pagination[data-collection="saved"]');
                if (result.data.pagination) {
                    if (!pagWrapper) {
                        pagWrapper = document.createElement('div');
                        pagWrapper.className = 'ua-pagination';
                        pagWrapper.dataset.collection = 'saved';
                        profileSavedContainer.appendChild(pagWrapper);
                    }
                    pagWrapper.innerHTML = result.data.pagination;
                } else if (pagWrapper) {
                    pagWrapper.remove();
                }

                profileSavedContainer.dataset.currentPage = result.data.current_page;
                profileSavedContainer.dataset.totalPages = result.data.total_pages;
                profileSavedContainer.dataset.activeFolder = result.data.folder;
                profileSavedContainer.dataset.activeLang = result.data.lang;
            }
        } catch (err) {
            console.error('Fetch saved failed:', err);
        }
    };

    if (savedSearchInput) {
        savedSearchInput.addEventListener('input', () => {
            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = setTimeout(() => {
                fetchSavedPage(1);
            }, 300);
        });
    }

    if (langToggleBtn) {
        langToggleBtn.addEventListener('click', () => {
            showAllLangs = !showAllLangs;
            langToggleBtn.classList.toggle('is-active', showAllLangs);
            filterContainer?.querySelectorAll('.saved-filter__btn.is-foreign-folder').forEach(btn => {
                btn.style.display = showAllLangs ? '' : 'none';
            });
            filterContainer?.querySelectorAll('.saved-filter__count').forEach(span => {
                const total = parseInt(span.dataset.total, 10) || 0;
                const native = parseInt(span.dataset.native, 10) || 0;
                span.textContent = showAllLangs ? total : native;
            });
            fetchSavedPage(1, null, showAllLangs ? 'all' : currentLang);
        });
    }

    // Filter by Folder tab click
    filterContainer?.addEventListener('click', (e) => {
        const btn = e.target.closest('.saved-filter__btn[data-folder]');
        if (!btn) return;
        if (e.target.closest('.folder-delete-action') || filterContainer.classList.contains('is-managing')) return;

        filterContainer.querySelectorAll('.saved-filter__btn[data-folder]').forEach(b => {
            b.classList.remove('is-active');
            b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        fetchSavedPage(1, btn.dataset.folder);
    });

    // Pagination link click delegation
    profileSavedContainer?.addEventListener('click', (e) => {
        const pageLink = e.target.closest('.ua-pagination[data-collection="saved"] a.page-numbers');
        if (!pageLink) return;
        e.preventDefault();
        const url = new URL(pageLink.href, window.location.origin);
        const page = url.searchParams.get('saved_page') || 1;
        fetchSavedPage(page);
        history.pushState(null, '', pageLink.href);
    });

    // Copy to clipboard (delegated)
    profileSavedContainer?.addEventListener('click', (e) => {
        const btn = e.target.closest('.saved-card__copy');
        if (!btn) return;
        const card = btn.closest('.saved-card');
        const text = card?.querySelector('.fragment-text')?.innerText || card?.querySelector('.saved-card__title a')?.innerText || '';
        navigator.clipboard.writeText(text).then(() => {
            const icon = btn.querySelector('i');
            if (icon) {
                const oldClass = icon.className;
                icon.className = 'fa-solid fa-check';
                setTimeout(() => icon.className = oldClass, 2000);
            }
        });
    });

    // Remove Saved Item (delegated)
    profileSavedContainer?.addEventListener('click', async (e) => {
        const btn = e.target.closest('.saved-card__remove');
        if (!btn) return;
        if (!confirm(config.i18n?.prof_confirm_remove_saved || 'Видалити цей елемент зі збереженого?')) return;
        const card = btn.closest('.saved-card');
        if (!card) return;
        const fd = new FormData();
        fd.append('action', 'united_auth_remove_saved');
        fd.append('id', card.dataset.id);
        fd.append('security', config.nonce);

        try {
            const response = await fetch(config.ajax_url, { method: 'POST', body: fd });
            const res = await response.json();
            if (res.success) {
                const folder = card.dataset.folder || 'General';
                const isNative = card.classList.contains('is-native');
                card.remove();
                adjustSavedTabCount('all', -1, isNative);
                adjustSavedTabCount(folder, -1, isNative);
                const remaining = profileSavedContainer.querySelectorAll('.saved-card');
                if (remaining.length === 0) {
                    const currentPage = parseInt(profileSavedContainer.dataset.currentPage, 10) || 1;
                    fetchSavedPage(Math.max(1, currentPage - 1));
                }
            }
        } catch (err) {
            console.error('Remove saved failed:', err);
        }
    });

    // Card Move & Edit (delegated)
    profileSavedContainer?.addEventListener('click', async (e) => {
        const editBtn = e.target.closest('.saved-card__edit');
        if (editBtn) {
            const card = editBtn.closest('.saved-card');
            const customSelect = card?.querySelector('.saved-folder-custom-select');
            const currentFolderSpan = card?.querySelector('.current-folder');
            if (card && customSelect) {
                const isEditing = card.classList.toggle('is-editing');
                customSelect.style.display = isEditing ? 'inline-flex' : 'none';
                if (currentFolderSpan) currentFolderSpan.style.display = isEditing ? 'none' : 'flex';
            }
            return;
        }

        const cancelBtn = e.target.closest('.folder-cancel-btn');
        if (cancelBtn) {
            const card = cancelBtn.closest('.saved-card');
            const customSelect = card?.querySelector('.saved-folder-custom-select');
            const currentFolderSpan = card?.querySelector('.current-folder');
            const moveInput = card?.querySelector('.folder-move-input');
            if (card && customSelect && moveInput) {
                const prev = moveInput.dataset.prev || 'General';
                moveInput.value = prev;
                const label = customSelect.querySelector('.custom-select__label');
                if (label) label.textContent = (prev === 'General' ? (config.i18n?.folder_general || 'Загальна') : prev);
                customSelect.querySelectorAll('.custom-select__option').forEach(opt => {
                    opt.classList.toggle('selected', opt.dataset.value === prev);
                });
                card.classList.remove('is-editing');
                customSelect.style.display = 'none';
                if (currentFolderSpan) currentFolderSpan.style.display = 'flex';
            }
            return;
        }

        const saveBtn = e.target.closest('.folder-save-btn');
        if (saveBtn) {
            const card = saveBtn.closest('.saved-card');
            const customSelect = card?.querySelector('.saved-folder-custom-select');
            const currentFolderSpan = card?.querySelector('.current-folder');
            const folderNameText = card?.querySelector('.folder-name-text');
            const moveInput = card?.querySelector('.folder-move-input');
            if (!card || !customSelect || !moveInput) return;

            const targetFolder = moveInput.value || 'General';
            const snippetId = card.dataset.id;
            const prev = moveInput.dataset.prev || 'General';

            if (targetFolder === prev) {
                card.classList.remove('is-editing');
                customSelect.style.display = 'none';
                if (currentFolderSpan) currentFolderSpan.style.display = 'flex';
                return;
            }

            saveBtn.disabled = true;
            const fd = new FormData();
            fd.append('action', 'united_auth_move_snippet');
            fd.append('snippet_id', snippetId);
            fd.append('folder', targetFolder);
            if (currentLang) fd.append('lang', currentLang);
            fd.append('security', config.nonce);

            try {
                const response = await fetch(config.ajax_url, { method: 'POST', body: fd });
                const res = await response.json();
                saveBtn.disabled = false;
                if (res.success) {
                    const finalFolder = res.data?.folder || targetFolder;
                    const isNative = card.classList.contains('is-native');
                    card.dataset.folder = finalFolder;
                    moveInput.dataset.prev = finalFolder;
                    moveInput.value = finalFolder;
                    if (folderNameText) {
                        folderNameText.textContent = (finalFolder === 'General' ? (config.i18n?.folder_general || 'Загальна') : finalFolder);
                    }
                    card.classList.remove('is-editing');
                    customSelect.style.display = 'none';
                    if (currentFolderSpan) currentFolderSpan.style.display = 'flex';

                    adjustSavedTabCount(prev, -1, isNative);
                    adjustSavedTabCount(finalFolder, +1, isNative);

                    const activeBtn = filterContainer?.querySelector('.saved-filter__btn.is-active[data-folder]');
                    const activeFolder = activeBtn ? activeBtn.dataset.folder : 'all';
                    if (activeFolder !== 'all' && activeFolder !== finalFolder) {
                        card.remove();
                        const remaining = profileSavedContainer.querySelectorAll('.saved-card');
                        if (remaining.length === 0) {
                            const currentPage = parseInt(profileSavedContainer.dataset.currentPage, 10) || 1;
                            fetchSavedPage(Math.max(1, currentPage - 1));
                        }
                    }
                } else {
                    alert(res.data?.message || 'Помилка переміщення');
                }
            } catch (err) {
                saveBtn.disabled = false;
                console.error('Move snippet failed:', err);
            }
        }
    });

    // --- Folder Management Strip ---
    if (filterContainer) {
        const manageBtn = filterContainer.querySelector('.saved-filter__manage-btn');
        const addBtn = filterContainer.querySelector('.saved-filter__add-btn');
        const addForm = filterContainer.querySelector('.saved-filter__add-form');
        const confirmAdd = addForm?.querySelector('.confirm');
        const cancelAdd = addForm?.querySelector('.cancel');
        const addInput = addForm?.querySelector('input');

        const closeAddForm = () => {
            addForm?.classList.remove('is-visible');
            if (addInput) addInput.value = '';
        };

        // Keyboard handler for folder input
        addInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                confirmAdd?.click();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                closeAddForm();
            }
        });

        // Toggle Rename/Delete Mode
        manageBtn?.addEventListener('click', () => {
            const isEditing = filterContainer.classList.toggle('is-managing');
            filterContainer.querySelectorAll('.folder-name').forEach(span => {
                const btn = span.closest('.saved-filter__btn');
                if (btn && btn.dataset.folder !== 'all' && btn.dataset.folder !== 'General') {
                    span.contentEditable = isEditing ? 'true' : 'false';
                    if (isEditing) {
                        span.dataset.oldName = span.textContent.trim();
                        span.addEventListener('keydown', handleRenameKeydown);
                        span.addEventListener('blur', handleRenameBlur);
                    } else {
                        span.removeEventListener('keydown', handleRenameKeydown);
                        span.removeEventListener('blur', handleRenameBlur);
                    }
                }
            });
            manageBtn.classList.toggle('is-active', isEditing);
            manageBtn.setAttribute('aria-pressed', isEditing ? 'true' : 'false');
        });

        const handleRenameKeydown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.target.blur();
            }
            if (e.key === 'Escape') {
                e.target.textContent = e.target.dataset.oldName;
                e.target.blur();
            }
        };

        const handleRenameBlur = async (e) => {
            const span = e.target;
            const newName = span.textContent.trim();
            const oldName = span.dataset.oldName;

            if (newName === oldName || !newName) {
                span.textContent = oldName;
                return;
            }

            const fd = new FormData();
            fd.append('action', 'united_auth_rename_folder');
            fd.append('old_name', oldName);
            fd.append('new_name', newName);
            if (currentLang) fd.append('lang', currentLang);
            fd.append('security', config.nonce);

            try {
                const response = await fetch(config.ajax_url, { method: 'POST', body: fd });
                const res = await response.json();
                if (res.success) {
                    span.dataset.oldName = newName;
                    const btn = span.closest('.saved-filter__btn');
                    if (btn) btn.dataset.folder = newName;

                    const isInScope = (item) => !currentLang || !item.dataset.lang || item.dataset.lang === currentLang;

                    // Update all cards in server scope without CSS selector injection
                    document.querySelectorAll('.saved-card').forEach(card => {
                        if (card.dataset.folder === oldName && isInScope(card)) {
                            card.dataset.folder = newName;
                            const nameEl = card.querySelector('.folder-name-text');
                            if (nameEl) nameEl.textContent = newName;
                        }
                    });

                    document.querySelectorAll('.saved-folder-custom-select .custom-select__option').forEach(opt => {
                        if (opt.dataset.value === oldName) {
                            opt.dataset.value = newName;
                            opt.textContent = newName;
                        }
                    });

                    updateFilterCounts();
                } else {
                    span.textContent = oldName;
                    if (res.data?.message) alert(res.data.message);
                }
            } catch (err) {
                span.textContent = oldName;
                console.error('Rename folder failed:', err);
            }
        };

        // Add Folder
        addBtn?.addEventListener('click', () => {
            addForm?.classList.add('is-visible');
            addInput?.focus();
        });

        cancelAdd?.addEventListener('click', () => {
            closeAddForm();
        });

        confirmAdd?.addEventListener('click', async () => {
            const name = addInput?.value.trim();
            if (!name) return;

            const fd = new FormData();
            fd.append('action', 'united_auth_create_folder');
            fd.append('folder_name', name);
            if (currentLang) fd.append('lang', currentLang);
            fd.append('security', config.nonce);

            try {
                const response = await fetch(config.ajax_url, { method: 'POST', body: fd });
                const res = await response.json();
                if (res.success) {
                    const folderName = res.data?.created || name;

                    // Check if tab already exists
                    let existingBtn = Array.from(filterContainer.querySelectorAll('.saved-filter__btn')).find(b => b.dataset.folder === folderName);
                    if (!existingBtn) {
                        const newBtn = document.createElement('button');
                        newBtn.className = 'saved-filter__btn is-native-folder';
                        newBtn.dataset.folder = folderName;
                        newBtn.setAttribute('type', 'button');
                        newBtn.setAttribute('role', 'tab');
                        newBtn.setAttribute('aria-selected', 'false');

                        const folderIcon = document.createElement('i');
                        folderIcon.className = 'fa-solid fa-folder';
                        newBtn.appendChild(folderIcon);

                        const folderNameSpan = document.createElement('span');
                        folderNameSpan.className = 'folder-name';
                        folderNameSpan.setAttribute('contenteditable', 'false');
                        folderNameSpan.textContent = folderName;
                        newBtn.appendChild(folderNameSpan);

                        const countSpan = document.createElement('span');
                        countSpan.className = 'saved-filter__count';
                        countSpan.dataset.total = '0';
                        countSpan.dataset.native = '0';
                        countSpan.textContent = '0';
                        newBtn.appendChild(countSpan);

                        const delSpan = document.createElement('span');
                        delSpan.className = 'folder-delete-action';
                        delSpan.title = config.i18n?.delete || 'Видалити';
                        delSpan.setAttribute('role', 'button');
                        delSpan.setAttribute('aria-label', config.i18n?.delete || 'Видалити');
                        const delIcon = document.createElement('i');
                        delIcon.className = 'fa-solid fa-xmark';
                        delSpan.appendChild(delIcon);
                        newBtn.appendChild(delSpan);

                        const controls = filterContainer.querySelector('.saved-filter__controls');
                        if (controls) {
                            filterContainer.insertBefore(newBtn, controls);
                        } else {
                            filterContainer.appendChild(newBtn);
                        }
                    }

                    // Add option to all custom-selects
                    document.querySelectorAll('.saved-folder-custom-select .custom-select__dropdown').forEach(dd => {
                        const exists = Array.from(dd.querySelectorAll('.custom-select__option')).some(opt => opt.dataset.value === folderName);
                        if (!exists) {
                            const opt = document.createElement('div');
                            opt.className = 'custom-select__option';
                            opt.dataset.value = folderName;
                            opt.textContent = folderName;
                            const newOpt = Array.from(dd.querySelectorAll('.custom-select__option')).find(o => o.dataset.value === '__new__');
                            if (newOpt) {
                                dd.insertBefore(opt, newOpt);
                            } else {
                                dd.appendChild(opt);
                            }
                        }
                    });

                    closeAddForm();
                } else {
                    alert(res.data?.message || 'Не вдалося створити папку');
                }
            } catch (err) {
                console.error('Create folder failed:', err);
            }
        });

        // Delete Folder
        filterContainer.addEventListener('click', async (e) => {
            const delBtn = e.target.closest('.folder-delete-action');
            if (!delBtn) return;

            const btn = delBtn.closest('.saved-filter__btn');
            const folder = btn?.dataset.folder;
            if (!folder || folder === 'all' || folder === 'General') return;

            if (!confirm(config.i18n?.prof_confirm_delete_folder || `Видалити спільну папку "${folder}" та всі нотатки й збереження в ній?`)) return;

            const fd = new FormData();
            fd.append('action', 'united_auth_delete_folder');
            fd.append('folder', folder);
            if (currentLang) fd.append('lang', currentLang);
            fd.append('security', config.nonce);

            try {
                const response = await fetch(config.ajax_url, { method: 'POST', body: fd });
                const res = await response.json();
                if (res.success) {
                    const wasActive = btn.classList.contains('is-active');
                    const delCountTotal = parseInt(btn.querySelector('.saved-filter__count')?.dataset.total, 10) || 0;
                    const delCountNative = parseInt(btn.querySelector('.saved-filter__count')?.dataset.native, 10) || 0;
                    btn.remove();

                    // Adjust all tab counts
                    const allBtn = Array.from(filterContainer.querySelectorAll('.saved-filter__btn')).find(b => b.dataset.folder === 'all');
                    if (allBtn) {
                        const allCountSpan = allBtn.querySelector('.saved-filter__count');
                        if (allCountSpan) {
                            let tot = Math.max(0, (parseInt(allCountSpan.dataset.total, 10) || 0) - delCountTotal);
                            let nat = Math.max(0, (parseInt(allCountSpan.dataset.native, 10) || 0) - delCountNative);
                            allCountSpan.dataset.total = tot;
                            allCountSpan.dataset.native = nat;
                            allCountSpan.textContent = showAllLangs ? tot : nat;
                        }
                    }

                    if (wasActive) {
                        if (allBtn) {
                            filterContainer.querySelectorAll('.saved-filter__btn').forEach(b => {
                                b.classList.remove('is-active');
                                b.setAttribute('aria-selected', 'false');
                            });
                            allBtn.classList.add('is-active');
                            allBtn.setAttribute('aria-selected', 'true');
                        }
                    }

                    // Remove option from dropdowns
                    document.querySelectorAll('.saved-folder-custom-select .custom-select__option').forEach(opt => {
                        if (opt.dataset.value === folder) opt.remove();
                    });

                    fetchSavedPage(1, wasActive ? 'all' : (filterContainer.querySelector('.saved-filter__btn.is-active[data-folder]')?.dataset.folder || 'all'));
                } else {
                    alert(res.data?.message || 'Не вдалося видалити папку');
                }
            } catch (err) {
                console.error('Delete folder failed:', err);
            }
        });
    }
};
